import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { listAPIKeys, getWidgets } from '../services/api';
import { getBots } from '../services/api/bots';
import { useUserSession } from './UserSessionContext';

const CACHE_TTL_MS = 60_000;

const ChannelsDataContext = createContext(null);

export function ChannelsDataProvider({ children }) {
  const { keycloak } = useKeycloak();
  const { isAdmin, rolesLoaded } = useUserSession();

  const [bots, setBots] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [botsLoading, setBotsLoading] = useState(false);
  const [widgetsLoading, setWidgetsLoading] = useState(false);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);

  const timestamps = useRef({ bots: 0, widgets: 0, apiKeys: 0 });
  const botsRef = useRef(bots);
  const widgetsRef = useRef(widgets);
  const apiKeysRef = useRef(apiKeys);
  botsRef.current = bots;
  widgetsRef.current = widgets;
  apiKeysRef.current = apiKeys;

  const inFlight = useRef({ bots: null, widgets: null, apiKeys: null });

  const isFresh = (key) => Date.now() - timestamps.current[key] < CACHE_TTL_MS;

  const ensureBots = useCallback(
    async (force = false) => {
      if (!keycloak?.authenticated) return botsRef.current;
      if (!force && timestamps.current.bots > 0 && isFresh('bots')) {
        return botsRef.current;
      }
      if (inFlight.current.bots) return inFlight.current.bots;

      setBotsLoading(true);
      const promise = (async () => {
        try {
          const data = await getBots(keycloak);
          const list = Array.isArray(data) ? data : [];
          setBots(list);
          timestamps.current.bots = Date.now();
          return list;
        } catch (e) {
          console.error('Failed to load bots:', e);
          return botsRef.current;
        } finally {
          setBotsLoading(false);
          inFlight.current.bots = null;
        }
      })();
      inFlight.current.bots = promise;
      return promise;
    },
    [keycloak]
  );

  const ensureWidgets = useCallback(
    async (force = false) => {
      if (!keycloak?.authenticated || !rolesLoaded) return widgetsRef.current;
      if (!force && timestamps.current.widgets > 0 && isFresh('widgets')) {
        return widgetsRef.current;
      }
      if (inFlight.current.widgets) return inFlight.current.widgets;

      setWidgetsLoading(true);
      const promise = (async () => {
        try {
          const data = await getWidgets(keycloak, null, { showAll: isAdmin });
          const list = Array.isArray(data) ? data : [];
          setWidgets(list);
          timestamps.current.widgets = Date.now();
          return list;
        } catch (e) {
          console.error('Failed to load widgets:', e);
          return widgetsRef.current;
        } finally {
          setWidgetsLoading(false);
          inFlight.current.widgets = null;
        }
      })();
      inFlight.current.widgets = promise;
      return promise;
    },
    [keycloak, isAdmin, rolesLoaded]
  );

  const ensureApiKeys = useCallback(
    async (force = false) => {
      if (!keycloak?.authenticated) return apiKeysRef.current;
      if (!force && timestamps.current.apiKeys > 0 && isFresh('apiKeys')) {
        return apiKeysRef.current;
      }
      if (inFlight.current.apiKeys) return inFlight.current.apiKeys;

      setApiKeysLoading(true);
      const promise = (async () => {
        try {
          const data = await listAPIKeys(keycloak);
          const list = Array.isArray(data) ? data : [];
          setApiKeys(list);
          timestamps.current.apiKeys = Date.now();
          return list;
        } catch (e) {
          console.error('Failed to load API keys:', e);
          return apiKeysRef.current;
        } finally {
          setApiKeysLoading(false);
          inFlight.current.apiKeys = null;
        }
      })();
      inFlight.current.apiKeys = promise;
      return promise;
    },
    [keycloak]
  );

  const invalidateBots = useCallback(() => {
    timestamps.current.bots = 0;
    inFlight.current.bots = null;
  }, []);

  const invalidateWidgets = useCallback(() => {
    timestamps.current.widgets = 0;
    inFlight.current.widgets = null;
  }, []);

  const invalidateApiKeys = useCallback(() => {
    timestamps.current.apiKeys = 0;
    inFlight.current.apiKeys = null;
  }, []);

  const value = useMemo(
    () => ({
      bots,
      widgets,
      apiKeys,
      botsLoading,
      widgetsLoading,
      apiKeysLoading,
      setBots,
      setWidgets,
      setApiKeys,
      ensureBots,
      ensureWidgets,
      ensureApiKeys,
      invalidateBots,
      invalidateWidgets,
      invalidateApiKeys,
    }),
    [
      bots,
      widgets,
      apiKeys,
      botsLoading,
      widgetsLoading,
      apiKeysLoading,
      ensureBots,
      ensureWidgets,
      ensureApiKeys,
      invalidateBots,
      invalidateWidgets,
      invalidateApiKeys,
    ]
  );

  return (
    <ChannelsDataContext.Provider value={value}>{children}</ChannelsDataContext.Provider>
  );
}

export function useChannelsData() {
  const ctx = useContext(ChannelsDataContext);
  if (!ctx) {
    throw new Error('useChannelsData must be used within ChannelsDataProvider');
  }
  return ctx;
}
